import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';
import { createCustomerBooking, getCustomerConnections } from '../services/customerService';
import { cylinderTypeApi } from '../../api/tenant/masterData.api'; // ✅ Use your existing API
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ✅ Same toast pattern as AddCustomer
const showToast = (type, message) => {
  const configs = {
    success: {
      color: "#28a745",
      border: "2px solid #28a745",
    },
    error: {
      color: "#dc3545",
      border: "2px solid #dc3545",
    },
    warning: {
      color: "#ffc107",
      border: "2px solid #ffc107",
    },
    info: {
      color: "#17a2b8",
      border: "2px solid #17a2b8",
    },
  };

  const config = configs[type];

  toast[type](message, {
    position: "top-right",
    autoClose: 3000,
    transition: Bounce,
    style: {
      border: config.border,
      background: "transparent",
      color: config.color,
      boxShadow: "none",
      marginTop: "70px",
    },
    icon: false,
  });
};

// ✅ Helper to extract data from API response
const extractData = (response) => {
  if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
    return response.data.data.data;
  }
  if (response?.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

const CreateBookingModal = ({ show, onHide, customerId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [cylinderTypes, setCylinderTypes] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  
  const [formData, setFormData] = useState({
    connection_id: '',
    cylinder_type_id: '',
    booking_source: 'walk-in',
    booking_date: new Date().toISOString().slice(0, 16),
    preferred_delivery_date: new Date().toISOString().split('T')[0],
    delivery_slot: 'anytime',
    quantity: 1,
    total_amount: 0,
    notes: '',
  });

  // Load data when modal opens
  useEffect(() => {
    if (show && customerId) {
      loadData();
    }
  }, [show, customerId]);

  const loadData = async () => {
    setLoadingData(true);
    try {
      // Load both connections and cylinder types in parallel
      const [connectionsData, cylinderTypesData] = await Promise.all([
        getCustomerConnections(customerId),
        cylinderTypeApi.getAll(),
      ]);

      setConnections(Array.isArray(connectionsData) ? connectionsData : []);
      
      // ✅ Extract cylinder types from response
      const types = extractData(cylinderTypesData);
      setCylinderTypes(Array.isArray(types) ? types : []);
      
      console.log('📦 Connections loaded:', connectionsData);
      console.log('📦 Cylinder types loaded:', types);
    } catch (error) {
      console.error('Failed to load data:', error);
      showToast('error', 'Failed to load required data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // ✅ Build payload with correct field names
      const payload = {
        customer_id: Number(customerId),
        connection_id: formData.connection_id ? Number(formData.connection_id) : null,
        cylinder_type_id: Number(formData.cylinder_type_id), // ✅ Required
        brand_id: null,
        booking_source: formData.booking_source,
        booking_date: formData.booking_date,
        preferred_delivery_date: formData.preferred_delivery_date,
        delivery_slot: formData.delivery_slot,
        delivery_instructions: null,
        quantity: Number(formData.quantity),
        unit_price: null,
        total_amount: Number(formData.total_amount),
        advance_paid: 0,
        booking_status: 'booked',
        notes: formData.notes || null,
        createdBy: 1,
      };

      console.log('📦 Creating booking with payload:', payload);
      
      const response = await createCustomerBooking(payload);
      console.log('✅ Booking created:', response);
      
      showToast('success', 'Booking created successfully! 🎉');
      
      // Reset form
      setFormData({
        connection_id: '',
        cylinder_type_id: '',
        booking_source: 'walk-in',
        booking_date: new Date().toISOString().slice(0, 16),
        preferred_delivery_date: new Date().toISOString().split('T')[0],
        delivery_slot: 'anytime',
        quantity: 1,
        total_amount: 0,
        notes: '',
      });
      
      if (onSuccess) onSuccess();
      onHide();
    } catch (error) {
      console.error('❌ Error creating booking:', error.response?.data || error);
      showToast('error', error.response?.data?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        transition={Bounce}
      />
      
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>New Booking</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {loadingData ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="secondary" />
                <p className="mt-2 text-muted">Loading data...</p>
              </div>
            ) : (
              <Row className="g-3">
                {/* Connection */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Connection</Form.Label>
                    <Form.Select
                      name="connection_id"
                      value={formData.connection_id}
                      onChange={handleChange}
                    >
                      <option value="">Select Connection</option>
                      {connections.map(conn => (
                        <option key={conn.id} value={conn.id}>
                          {conn.connection_number} - {conn.cylinder_capacity}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* ✅ Cylinder Type - Now using cylinder_type_id */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Cylinder Type *</Form.Label>
                    <Form.Select
                      name="cylinder_type_id"
                      value={formData.cylinder_type_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Cylinder Type</option>
                      {cylinderTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name} ({type.capacity_kg || type.weight}kg) - ₹{type.price || 0}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Booking Source */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Booking Source</Form.Label>
                    <Form.Select
                      name="booking_source"
                      value={formData.booking_source}
                      onChange={handleChange}
                    >
                      <option value="walk-in">Walk-in</option>
                      <option value="phone">Phone</option>
                      <option value="mobile_app">Mobile App</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="website">Website</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Quantity */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      min="1"
                      max="10"
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Booking Date */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Booking Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      name="booking_date"
                      value={formData.booking_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Preferred Delivery Date */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Preferred Delivery Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="preferred_delivery_date"
                      value={formData.preferred_delivery_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Delivery Slot */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Delivery Slot</Form.Label>
                    <Form.Select
                      name="delivery_slot"
                      value={formData.delivery_slot}
                      onChange={handleChange}
                    >
                      <option value="anytime">Anytime</option>
                      <option value="morning">Morning (9AM - 12PM)</option>
                      <option value="afternoon">Afternoon (12PM - 4PM)</option>
                      <option value="evening">Evening (4PM - 8PM)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Total Amount */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Total Amount (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      name="total_amount"
                      value={formData.total_amount}
                      onChange={handleChange}
                      min="0"
                      step="10"
                      placeholder="Enter total amount"
                    />
                  </Form.Group>
                </Col>

                {/* Notes */}
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Additional notes..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>
              <FaTimes className="me-2" /> Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || loadingData}
              style={{
                backgroundColor: "rgb(30, 58, 111)",
                border: "none",
                borderRadius: "30px",
                padding: "10px 24px",
              }}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <><FaSave className="me-2" /> Create Booking</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default CreateBookingModal;